import axios from 'axios';
import { API_BASE_URL } from './apiConfig';

// Version: 1.3.0 - Added copyMyApiKey endpoint for copying full API key
// Last updated: 2025-12-02

// Create axios instance with base configuration
const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to add token to headers
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
authApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('sessionId');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Login endpoint
const login = async (username, password) => {
  try {
    const response = await authApi.post('/api/v2/auth/login', {
      username,
      password
    });
    return response.data;
  } catch (error) {
    console.error('Login API error:', error);
    throw error;
  }
};

// Logout endpoint
const logout = async () => {
  try {
    const response = await authApi.post('/api/v2/auth/logout', {});
    return response.data;
  } catch (error) {
    console.error('Logout API error:', error);
    throw error;
  }
};

// Logout from all sessions
const logoutAll = async () => {
  try {
    const response = await authApi.post('/api/v2/auth/logout-all', {});
    return response.data;
  } catch (error) {
    console.error('Logout all API error:', error);
    throw error;
  }
};

// Get user profile
const getUserProfile = async () => {
  try {
    const response = await authApi.get('/api/v2/auth/profile');
    return response.data;
  } catch (error) {
    console.error('Get user profile API error:', error);
    throw error;
  }
};

// Update user profile
const updateUserProfile = async (userData) => {
  try {
    const response = await authApi.put('/api/v2/auth/profile', userData);
    return response.data;
  } catch (error) {
    console.error('Update user profile API error:', error);
    throw error;
  }
};

// Change password endpoint
const changePassword = async (oldPassword, newPassword) => {
  try {
    const response = await authApi.put('/api/v2/auth/profile/password', {
      oldPassword,
      newPassword
    });
    return response.data;
  } catch (error) {
    console.error('Change password API error:', error);
    throw error;
  }
};

// Get API key info for current user (returns MASKED key)
const getMyApiKey = async () => {
  try {
    const response = await authApi.get('/api/v2/auth/profile/api-key');
    return response.data;
  } catch (error) {
    console.error('Get API key error:', error);
    throw error;
  }
};

// Copy API key - Get FULL API key for copying to clipboard (admin only)
const copyMyApiKey = async () => {
  try {
    const response = await authApi.post('/api/v2/auth/profile/api-key/copy', {});
    return response.data;
  } catch (error) {
    console.error('Copy API key error:', error);
    throw error;
  }
};

// Generate API key for current user (admin only)
const generateMyApiKey = async () => {
  try {
    const response = await authApi.post('/api/v2/auth/profile/api-key', {});
    return response.data;
  } catch (error) {
    console.error('Generate API key error:', error);
    throw error;
  }
};

// Revoke API key for current user
const revokeMyApiKey = async () => {
  try {
    const response = await authApi.delete('/api/v2/auth/profile/api-key');
    return response.data;
  } catch (error) {
    console.error('Revoke API key error:', error);
    throw error;
  }
};

// User settings endpoints
const getUserSettings = async () => {
  try {
    const response = await authApi.get('/api/v2/auth/settings');
    return response.data;
  } catch (error) {
    console.error('Get user settings error:', error);
    throw error;
  }
};

const updateUserSettings = async (settings) => {
  try {
    const response = await authApi.put('/api/v2/auth/settings', settings);
    return response.data;
  } catch (error) {
    console.error('Update user settings error:', error);
    throw error;
  }
};

// User Management - Admin only
// Get all users
const getAllUsers = async () => {
  try {
    const response = await authApi.get('/api/v2/auth/users');
    return response.data;
  } catch (error) {
    console.error('Get all users API error:', error);
    throw error;
  }
};

// Get user by ID
const getUserById = async (userId) => {
  try {
    const response = await authApi.get(`/api/v2/auth/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Get user by ID API error:', error);
    throw error;
  }
};

// Create new user (Admin)
const createUser = async (userData) => {
  try {
    const response = await authApi.post('/api/v2/auth/users', userData);
    return response.data;
  } catch (error) {
    console.error('Create user API error:', error);
    throw error;
  }
};

// Update user (Admin)
const updateUser = async (userId, userData) => {
  try {
    const response = await authApi.put(`/api/v2/auth/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error('Update user API error:', error);
    throw error;
  }
};

// Delete user (Admin)
const deleteUser = async (userId) => {
  try {
    const response = await authApi.delete(`/api/v2/auth/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Delete user API error:', error);
    throw error;
  }
};

// Register new user (public registration if enabled)
const register = async (userData) => {
  try {
    const response = await authApi.post('/api/v2/auth/register', userData);
    return response.data;
  } catch (error) {
    console.error('Register API error:', error);
    throw error;
  }
};

// Forgot password
const forgotPassword = async (email) => {
  try {
    const response = await authApi.post('/api/v2/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    console.error('Forgot password API error:', error);
    throw error;
  }
};

// Reset password with token
const resetPassword = async (token, newPassword) => {
  try {
    const response = await authApi.post('/api/v2/auth/reset-password', {
      token,
      newPassword
    });
    return response.data;
  } catch (error) {
    console.error('Reset password API error:', error);
    throw error;
  }
};

// Verify email
const verifyEmail = async (token) => {
  try {
    const response = await authApi.post('/api/v2/auth/verify-email', { token });
    return response.data;
  } catch (error) {
    console.error('Verify email API error:', error);
    throw error;
  }
};

// Set axios default headers (utility function)
const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
    delete authApi.defaults.headers.common['Authorization'];
  }
};

// Get axios instance (for custom requests)
const getAxiosInstance = () => authApi;

// User Authentication API endpoints object
const LoginAuthUser = {
  login,
  logout,
  logoutAll,
  getUserProfile,
  updateUserProfile,
  changePassword,
  getMyApiKey,
  copyMyApiKey,
  generateMyApiKey,
  revokeMyApiKey,
  getUserSettings,
  updateUserSettings,
  getAllUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  register,
  forgotPassword,
  resetPassword,
  verifyEmail,
  setAuthToken,
  getAxiosInstance
};

export default LoginAuthUser;

// Named exports for direct imports
export {
  login,
  logout,
  logoutAll,
  getUserProfile,
  updateUserProfile,
  changePassword,
  getMyApiKey,
  copyMyApiKey,
  generateMyApiKey,
  revokeMyApiKey,
  getUserSettings,
  updateUserSettings,
  getAllUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  register,
  forgotPassword,
  resetPassword,
  verifyEmail,
  setAuthToken,
  getAxiosInstance
};
